import React from 'react';
import { Person } from '../types';
import { Trash2, Baby, Mail, ChevronRight, Edit3, Target, ShieldCheck, User, TrendingUp, Layers } from 'lucide-react';

interface NomineeCardProps {
  person: Person & { assetCount?: number };
  effectiveShare: number;
  totalValue: number;
  onEdit: (person: Person) => void;
  onDelete: (id: string) => void;
}

export const NomineeCard: React.FC<NomineeCardProps> = ({ person, effectiveShare, totalValue, onEdit, onDelete }) => {
  return (
    <div className="bg-white rounded-[32px] shadow-[0px_18px_40px_rgba(112,144,176,0.12)] p-8 transition-all duration-300 hover:shadow-[0px_25px_50px_rgba(112,144,176,0.2)] hover:-translate-y-1 border border-transparent hover:border-indigo-100 flex flex-col h-full group relative overflow-hidden">
      {/* Decorative Background Accents */}
      <div className="absolute -top-12 -right-12 w-48 h-48 bg-indigo-50/50 rounded-full blur-3xl group-hover:scale-125 transition-transform duration-700 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-indigo-50/30 rounded-full blur-2xl group-hover:translate-x-4 transition-transform duration-700 pointer-events-none" />
      
      <div className="flex items-start justify-between mb-10 relative z-10">
        <div className="flex items-center gap-6">
          <div className="w-16 h-16 rounded-[24px] bg-indigo-600 flex items-center justify-center text-white font-black text-3xl shadow-lg shadow-indigo-100 border-2 border-white">
            {person.fullName.charAt(0)}
          </div>
          <div className="min-w-0">
            <h3 className="text-xl font-black text-[#2B3674] tracking-tight truncate leading-tight mb-1">
              {person.fullName}
            </h3>
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{person.relationship}</span>
              {person.isMinor && (
                <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-600 rounded-lg border border-amber-100">
                   <Baby size={12} strokeWidth={2.5} />
                   <span className="text-[9px] font-black uppercase tracking-tighter">Minor</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 bg-[#F4F7FE] backdrop-blur-sm rounded-2xl p-1.5 shadow-sm border border-gray-100 opacity-0 group-hover:opacity-100 transition-all translate-y-2 group-hover:translate-y-0 duration-300">
          <button onClick={() => onEdit(person)} className="p-2.5 text-gray-400 hover:text-indigo-600 hover:bg-white rounded-xl transition-all">
            <Edit3 size={18} />
          </button>
          <button onClick={() => onDelete(person.id)} className="p-2.5 text-gray-400 hover:text-rose-500 hover:bg-white rounded-xl transition-all">
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5 mb-10 relative z-10 flex-1">
        <div className="bg-[#F4F7FE] p-6 rounded-[28px] border border-transparent hover:border-indigo-100 transition-colors flex flex-col justify-center">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
            <Target size={14} className="text-indigo-400" /> Estate Share
          </p>
          <p className="text-3xl font-black text-[#2B3674] leading-none">
            {effectiveShare.toFixed(1)}<span className="text-sm font-bold ml-1 opacity-40">%</span>
          </p>
        </div>
        <div className="bg-[#F4F7FE] p-6 rounded-[28px] border border-transparent hover:border-indigo-100 transition-colors flex flex-col justify-center">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
            <TrendingUp size={14} className="text-indigo-400" /> Allocated Value
          </p>
          <p className="text-3xl font-black text-indigo-600 leading-none">
            <span className="text-base font-bold mr-0.5 opacity-60">₹</span>
            {totalValue >= 10000000 ? `${(totalValue/10000000).toFixed(2)}Cr` : totalValue >= 100000 ? `${(totalValue/100000).toFixed(1)}L` : totalValue >= 1000 ? `${(totalValue/1000).toFixed(0)}k` : totalValue}
          </p>
        </div>
      </div>

      {/* Asset Count Integration */}
      <div className="mb-8 relative z-10">
         <div className="bg-indigo-50/40 px-5 py-4 rounded-2xl flex items-center justify-between border border-indigo-100/50">
            <div className="flex items-center gap-3">
               <Layers size={16} className="text-indigo-600" />
               <span className="text-[11px] font-bold text-[#2B3674] uppercase tracking-widest">Asset Coverage</span>
            </div>
            <span className="text-xs font-black text-indigo-600 bg-white px-3 py-1 rounded-full shadow-sm">{person.assetCount || 0} Assets Linked</span>
         </div>
      </div>

      <div className="mt-auto pt-8 border-t border-gray-100 flex items-center justify-between relative z-10">
        <div className="flex items-center gap-3 text-gray-400 group-hover:text-indigo-600 transition-all overflow-hidden bg-[#F4F7FE]/50 px-4 py-2 rounded-2xl border border-transparent group-hover:border-indigo-50 max-w-[70%]">
          <Mail size={14} className="shrink-0 group-hover:scale-110 transition-transform" />
          <span className="text-[11px] font-black truncate tracking-tight uppercase leading-none">{person.email}</span>
        </div>
        <button onClick={() => onEdit(person)} className="flex items-center gap-2 group-hover:translate-x-1 transition-transform">
           <span className="text-[11px] font-black text-gray-300 group-hover:text-indigo-600 transition-colors uppercase tracking-widest">Review Profile</span>
           <ChevronRight size={18} className="text-gray-300 group-hover:text-indigo-600 transition-colors" strokeWidth={3} />
        </button>
      </div>
    </div>
  );
};